import React, { useEffect, useState, createContext, useContext } from 'react';
type CurrencyInfo = {
  code: string;
  symbol: string;
  rate: number;
};
const CURRENCY_MAP: Record<string, CurrencyInfo> = {
  US: {
    code: 'USD',
    symbol: '$',
    rate: 1
  },
  MX: {
    code: 'MXN',
    symbol: 'MX$',
    rate: 17.5
  },
  AR: {
    code: 'ARS',
    symbol: 'ARS$',
    rate: 900
  },
  CO: {
    code: 'COP',
    symbol: 'COP$',
    rate: 4200
  },
  CL: {
    code: 'CLP',
    symbol: 'CLP$',
    rate: 950
  },
  PE: {
    code: 'PEN',
    symbol: 'S/',
    rate: 3.75
  },
  UY: {
    code: 'UYU',
    symbol: 'UY$',
    rate: 40
  },
  BR: {
    code: 'BRL',
    symbol: 'R$',
    rate: 5.1
  }
};
type CurrencyContextType = {
  currency: CurrencyInfo;
  formatPrice: (usdAmount: number) => string;
  isLoading: boolean;
};
const defaultCurrency = CURRENCY_MAP.US;
const CurrencyContext = createContext<CurrencyContextType>({
  currency: defaultCurrency,
  formatPrice: amount => `$${amount.toFixed(2)}`,
  isLoading: true
});
export const useCurrency = () => useContext(CurrencyContext);
export const CurrencyProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const [currency, setCurrency] = useState<CurrencyInfo>(defaultCurrency);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const fetchCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        if (response.ok) {
          const data = await response.json();
          const countryCode = data.country_code;
          if (countryCode && CURRENCY_MAP[countryCode]) {
            setCurrency(CURRENCY_MAP[countryCode]);
          }
        }
      } catch (error) {
        console.error('Error fetching country data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCountry();
  }, []);
  const formatPrice = (usdAmount: number) => {
    const convertedAmount = usdAmount * currency.rate;
    // Format based on currency
    if (currency.code === 'USD') {
      // Keep decimals for USD if it's not a whole number
      return `${currency.symbol}${Number.isInteger(usdAmount) ? convertedAmount : convertedAmount.toFixed(2)}`;
    } else {
      // Round to whole numbers for other currencies to look cleaner
      return `${currency.symbol}${Math.round(convertedAmount).toLocaleString('es-AR')}`;
    }
  };
  return <CurrencyContext.Provider value={{
    currency,
    formatPrice,
    isLoading
  }} data-id="element-431">
      {children}
    </CurrencyContext.Provider>;
};